import { useState, useEffect } from 'react';
import { supportAPI } from '../api/services';
import Loading from '../components/common/Loading';
import toast from 'react-hot-toast';
import { FiMessageSquare, FiPlus, FiArrowLeft } from 'react-icons/fi';
import './Support.css';

export default function Support() {
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ subject: '', message: '', category: 'ORDER' });
  const [reply, setReply] = useState('');

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = () => {
    supportAPI.getAll().then(res => setTickets(res.data.data.content || [])).catch(() => {}).finally(() => setLoading(false));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await supportAPI.create(form);
      loadTickets();
      setShowCreate(false);
      setForm({ subject: '', message: '', category: 'ORDER' });
      toast.success('Ticket created');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    }
  };

  const loadTicketDetail = async (id) => {
    try {
      const res = await supportAPI.getById(id);
      setSelectedTicket(res.data.data);
    } catch (err) {
      toast.error('Failed to load ticket');
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    try {
      await supportAPI.addMessage(selectedTicket.id, { message: reply });
      await loadTicketDetail(selectedTicket.id);
      setReply('');
      toast.success('Reply sent');
    } catch (err) {
      toast.error('Failed to send reply');
    }
  };

  if (loading) return <Loading />;

  if (selectedTicket) {
    return (
      <div className="support-page">
        <button className="back-btn" onClick={() => setSelectedTicket(null)}>
          <FiArrowLeft /> Back to Tickets
        </button>
        <div className="ticket-detail">
          <div className="td-header">
            <h2>{selectedTicket.subject}</h2>
            <span className={`ticket-status status-${selectedTicket.status?.toLowerCase()}`}>{selectedTicket.status}</span>
          </div>
          <p className="td-meta">Category: {selectedTicket.category} · Created: {new Date(selectedTicket.createdAt).toLocaleDateString()}</p>
          <div className="td-messages">
            {selectedTicket.messages?.map((msg, i) => (
              <div key={i} className={`td-msg ${msg.isAdminReply ? 'admin' : 'user'}`}>
                <p className="msg-sender">{msg.isAdminReply ? 'Support Team' : 'You'}</p>
                <p className="msg-text">{msg.message}</p>
                <span className="msg-date">{new Date(msg.createdAt).toLocaleString()}</span>
              </div>
            ))}
          </div>
          {selectedTicket.status !== 'CLOSED' && (
            <form className="reply-form" onSubmit={handleReply}>
              <textarea placeholder="Type your reply..." value={reply} onChange={e => setReply(e.target.value)} rows={3} />
              <button type="submit" className="btn btn-primary btn-sm">Send Reply</button>
            </form>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="support-page">
      <div className="sp-header">
        <h1>Support Tickets</h1>
        <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(!showCreate)}>
          <FiPlus /> {showCreate ? 'Cancel' : 'New Ticket'}
        </button>
      </div>
      {showCreate && (
        <form className="create-ticket-form" onSubmit={handleCreate}>
          <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
            <option value="ORDER">Order Issue</option>
            <option value="PRODUCT">Product Inquiry</option>
            <option value="PAYMENT">Payment Issue</option>
            <option value="SHIPPING">Shipping Issue</option>
            <option value="OTHER">Other</option>
          </select>
          <input required placeholder="Subject" value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} />
          <textarea required placeholder="Describe your issue..." value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} rows={4} />
          <button type="submit" className="btn btn-primary btn-sm">Submit Ticket</button>
        </form>
      )}
      {tickets.length === 0 ? (
        <div className="empty-state">
          <FiMessageSquare size={48} />
          <h2>No support tickets</h2>
          <p>Need help? Create a new support ticket.</p>
        </div>
      ) : (
        <div className="tickets-list">
          {tickets.map(t => (
            <div key={t.id} className="ticket-card" onClick={() => loadTicketDetail(t.id)}>
              <div className="tc-main">
                <h3>{t.subject}</h3>
                <p className="tc-meta">{t.category} · {new Date(t.createdAt).toLocaleDateString()}</p>
              </div>
              <span className={`ticket-status status-${t.status?.toLowerCase()}`}>{t.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
